import { Controller } from "@hotwired/stimulus"

// Handles product search for the shopping list add-item form.
// Allows selecting an existing product (sets hidden product_id + name)
// or typing a freeform name (no product_id).
export default class extends Controller {
  static targets = ["input", "results", "productId", "nameField", "selectedBadge"]
  static values = { url: String }

  connect() {
    this.debounceTimer = null
  }

  disconnect() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
  }

  // Triggered on input in the search field
  search() {
    const query = this.inputTarget.value.trim()

    if (this.debounceTimer) clearTimeout(this.debounceTimer)

    // Clear product_id when user types (they may be changing from a selected product)
    this.productIdTarget.value = ""
    this.selectedBadgeTarget.style.display = "none"
    this.selectedBadgeTarget.innerHTML = ""
    this.inputTarget.style.display = "block"

    // Update the hidden name field to match what's typed
    this.nameFieldTarget.value = query

    if (query.length < 2) {
      this.resultsTarget.style.display = "none"
      this.resultsTarget.innerHTML = ""
      return
    }

    this.debounceTimer = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  async performSearch(query) {
    try {
      const url = `${this.urlValue}?q=${encodeURIComponent(query)}`
      const response = await fetch(url, {
        headers: { "Accept": "text/html" }
      })

      if (response.ok) {
        const html = await response.text()
        // Rewrite the data-action attributes to use our controller instead of product-search
        const modifiedHtml = html.replace(/product-search#selectProduct/g, "shopping-search#selectProduct")
        this.resultsTarget.innerHTML = modifiedHtml
        this.resultsTarget.style.display = modifiedHtml.trim().length > 0 ? "block" : "none"
      }
    } catch (error) {
      console.error("Shopping search error:", error)
    }
  }

  // When a product is selected from search results
  selectProduct(event) {
    const button = event.currentTarget
    const productId = button.dataset.productId
    const productName = button.dataset.productName

    // Set hidden fields
    this.productIdTarget.value = productId
    this.nameFieldTarget.value = productName

    // Show selected badge, hide search input
    this.selectedBadgeTarget.innerHTML = `
      <span class="badge bg-success d-inline-flex align-items-center gap-1">
        ${this.escapeHtml(productName)}
        <button type="button" class="btn-close btn-close-white ms-1" style="font-size: 0.5rem;"
                data-action="shopping-search#clearSelection"></button>
      </span>
    `
    this.selectedBadgeTarget.style.display = "inline-block"
    this.inputTarget.style.display = "none"

    // Hide results
    this.resultsTarget.style.display = "none"
    this.resultsTarget.innerHTML = ""
  }

  // Clear selected product and go back to freeform input
  clearSelection() {
    this.productIdTarget.value = ""
    this.nameFieldTarget.value = ""
    this.selectedBadgeTarget.style.display = "none"
    this.selectedBadgeTarget.innerHTML = ""
    this.inputTarget.value = ""
    this.inputTarget.style.display = "block"
    this.inputTarget.focus()
  }

  // Reset the form after successful submission (called externally if needed)
  reset() {
    this.clearSelection()
    this.resultsTarget.style.display = "none"
    this.resultsTarget.innerHTML = ""
  }

  // Close results when clicking outside
  closeResults(event) {
    if (!this.element.contains(event.target)) {
      this.resultsTarget.style.display = "none"
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
